/**
 * GoalLine — die Ziel-Zeile, wie sie auf der Seite „Umsetzungsgrad" überall
 * gleich aussieht: am Knoten der Architekturübersicht und in den Abschnitten
 * darunter.
 *
 * Sie besteht aus ZWEI getrennten Teilen, und diese Trennung ist der ganze
 * Punkt:
 *
 *  1. **Das Ziel** — `CONUT-nnn · Zieldatum · Status`. Kommt aus Atlassian,
 *     unverändert. Ohne Mapping steht dort „kein Ziel hinterlegt"; das ist
 *     eine Aussage und keine Lücke.
 *  2. **Der abgeleitete Grad** — eigene Zelle, eigene Beschriftung
 *     („abgeleitet"), eigene Herleitung (`grade.ts`). Er stammt aus UNSEREN
 *     Daten und darf nie so aussehen, als käme er aus dem Ziel. Deshalb steht
 *     das Wort „abgeleitet" sichtbar daran und nicht nur im Tooltip.
 *
 * Der Schlüssel verlinkt in die Roadmap-App (`roadmap_url`). Bewusst ein echtes
 * `<a>`: die Zeile ist Geschwister des Knotenkopfs, nie darin — verschachtelte
 * interaktive Elemente wären ungültiges Markup (dieselbe Regel wie in
 * `FlowNode`).
 */
import { STATUS_LABEL } from "@/components/GoalBadge";
import { findGoal, formatGoalDate, gradeForRef, type Grade } from "./grade";
import "./goals.css";

/** Die abgeleitete Zelle — überall dieselbe, immer beschriftet. */
export function GradeCell({ grade }: { grade: Grade }) {
  const known = grade.percent != null;
  return (
    <span
      className="grade"
      data-derivable={known ? "" : undefined}
      title={`Von uns abgeleitet, nicht aus dem Ziel: ${grade.detail}`}
    >
      <span className="grade-label">abgeleitet</span>
      {known ? (
        <>
          <span className="grade-bar" aria-hidden="true">
            <span className="grade-fill" style={{ width: `${grade.percent}%` }} />
          </span>
          <span className="grade-pct">{`${grade.percent} %`}</span>
          <span className="grade-basis">{grade.basis}</span>
        </>
      ) : (
        <span className="grade-none">nicht ableitbar</span>
      )}
    </span>
  );
}

/** Nur der Ziel-Teil — ohne den abgeleiteten Grad. */
export function GoalFacts({ refId }: { refId: string }) {
  const goal = findGoal(refId);
  if (!goal) {
    return <span className="goalline-empty">kein Ziel hinterlegt</span>;
  }
  const status = (goal.status as string | null) ?? "pending";
  const label = STATUS_LABEL[status] ?? status;
  const key = goal.goal_key ?? "Ziel folgt";
  return (
    <>
      <span className="goalline-dot" data-goal={status} aria-hidden="true" />
      {goal.goal_key && goal.roadmap_url ? (
        <a
          className="goalline-key"
          href={goal.roadmap_url}
          target="_blank"
          rel="noopener noreferrer"
          title={`${goal.name ?? key} — öffnet das Ziel in der Roadmap`}
        >
          {key}
        </a>
      ) : (
        <span className="goalline-key">{key}</span>
      )}
      <span className="goalline-date">{`bis ${formatGoalDate(goal.target_date)}`}</span>
      <span className="goalline-status" data-goal={status}>
        {label}
      </span>
    </>
  );
}

/**
 * Ziel + abgeleiteter Grad als zweizeiliger Block unter einem Knoten.
 * `data-goal-ref` ist der Nachweis-Anker: ein Test zählt darüber, dass jedes
 * Ziel aus `goals.json` auf der Seite wirklich erreichbar ist.
 */
export function GoalLine({ refId }: { refId: string }) {
  return (
    <span className="goalline" data-goal-ref={refId}>
      <span className="goalline-goal">
        <GoalFacts refId={refId} />
      </span>
      <GradeCell grade={gradeForRef(refId)} />
    </span>
  );
}
